import React, { useContext, useEffect, useState, useRef } from 'react'
import { Grid } from '@mui/material'
import { useGetPriceSheetsQuery } from 'services/priceSheets'
import ContentLayoutContext from 'components/layouts/ContentLayoutContextProvider'
import PriceListsSideNav from 'components/containers/commodities/price-lists/components/PriceListsSideNav'
import PriceListsSubContainer from 'components/containers/commodities/price-lists/components/PriceListsSubContainer'
import useGlobalLoader from 'utils/custom-hooks/useGlobalLoader'
import { useDispatch, useSelector } from 'react-redux'
import { CloudUpload as CloudUploadIcon, CloudDownload as CloudDownloadIcon } from '@mui/icons-material'
import { useGetPriceSheetCsvMultiLazyQuery, usePutPriceSheetCsvMultiMutation } from 'services/commodities'
import { dispatchSnackbar, downloader } from 'utils/helpers'
import { trackEvent } from 'utils/tracking'
import { CHURN_ZERO_EVENTS as cz } from 'utils/constants'
import PriceListUploadErrorsBlade from './components/PriceListUploadErrorsBlade'
import { useContentHeight } from 'utils/custom-hooks/useContentHeight'

const PriceListsContainer = () => {
  const context = useContext(ContentLayoutContext)
  const { contentHeight } = useContentHeight()

  const { data: priceSheetsData, isLoading: isLoadingPriceSheetsData } = useGetPriceSheetsQuery({
    filter: JSON.stringify({ type: 'PRICE_LIST' }),
  })

  const [getPriceSheetCsvMulti, { data: csvData, isLoading: isLoadingGetCsv, isError, error: getCsvError, isSuccess }] =
    useGetPriceSheetCsvMultiLazyQuery()

  const [putPriceSheetCsvMulti, { isLoading: isLoadingPutCsv }] = usePutPriceSheetCsvMultiMutation()

  useGlobalLoader(isLoadingPriceSheetsData || isLoadingGetCsv || isLoadingPutCsv)

  const [selectedPriceListId, setSelectedPriceListId] = useState('')
  const [priceSheetList, setPriceSheetList] = useState([])
  const [uploadErrorsList, setUploadErrorsList] = useState([])
  const dispatch = useDispatch()

  const handleUploadError = error => {
    // Parse data as JSON. This is necessary because the route is set to return raw data
    const data = error?.data ? JSON.parse(error.data) : []
    const errorList = data.errors.slice(null, 10)
    setUploadErrorsList(errorList)

    trackEvent({
      eventName: cz.UPLOAD_PRICE_SHEET_CSV,
    })
  }

  const handleDownloadError = () => {
    const errorMsg = 'CSV download failed.'

    // TODO Should we track upload/download times?
    trackEvent({
      eventName: cz.DOWNLOAD_PRICE_SHEET_CSV,
      errorMsg,
    })
    dispatchSnackbar(dispatch, 'error', errorMsg)
  }

  const handleSuccess = ({ isUpload }) => {
    trackEvent(isUpload ? cz.UPLOAD_PRICE_SHEET_CSV : cz.DOWNLOAD_PRICE_SHEET_CSV)
    setUploadErrorsList([])
    dispatchSnackbar(dispatch, 'success', `CSV file ${isUpload ? 'uploaded' : 'downloaded'} successfully`)
  }

  const handleGetPriceSheetCsv = () => getPriceSheetCsvMulti({})

  const { organization, selectedYard } = useSelector(state => state.auth)

  useEffect(() => {
    if (!csvData) return

    const today = new Date()

    // replace any non-alphanumeric characters with underscores
    downloader(
      csvData,
      'text/csv',
      `${organization}-${selectedYard}-${today.toISOString().split('T')[0]}-price-lists.csv`,
    )
  }, [csvData])

  useEffect(() => {
    if (isSuccess) {
      handleSuccess({ isUpload: false })
    }
    if (isError) {
      handleDownloadError(getCsvError)
    }
  }, [isError, isSuccess])

  const uploaderBtnRef = useRef()
  const handlePutCsv = () => {
    uploaderBtnRef.current.click()
  }

  const submitCsvFile = async event => {
    const selectedCsv = event.target.files[0]
    if (!selectedCsv) return

    const file = new FormData()
    file.append('data', selectedCsv, 'priceSheetFile.csv')
    await putPriceSheetCsvMulti({ body: file })
      .unwrap()
      .then(() => {
        handleSuccess({ isUpload: true })
      })
      .catch(err => {
        handleUploadError(err)
      })
  }

  const csvActions = [
    {
      icon: <CloudUploadIcon />,
      text: 'Upload Prices',
      id: 'upload-prices',
      variant: 'text',
      disabled: isLoadingGetCsv || isLoadingPutCsv,
      onClick: handlePutCsv,
    },
    {
      icon: <CloudDownloadIcon />,
      text: 'Download Prices',
      id: 'download-prices',
      variant: 'text',
      disabled: isLoadingGetCsv || isLoadingPutCsv,
      onClick: handleGetPriceSheetCsv,
    },
  ]

  useEffect(() => {
    context.setContainerTitle('Commodities')
    context.setContainerActions(csvActions)
  }, [])

  useEffect(() => {
    if (priceSheetsData?.Items?.length > 0) {
      const filteredPriceSheets = priceSheetsData.Items.slice().sort((a, b) => {
        if (!a.displayName) return 1
        if (!b.displayName) return -1
        return a.displayName.localeCompare(b.displayName)
      })
      setPriceSheetList(filteredPriceSheets)
    }
  }, [priceSheetsData])

  return (
    <>
      <input
        type="file"
        accept=".csv"
        style={{ display: 'none' }}
        ref={uploaderBtnRef}
        onChange={e => {
          submitCsvFile(e)
        }}
      />
      <Grid container pt={1} pb={1} backgroundColor="background.section" minHeight={contentHeight}>
        <PriceListUploadErrorsBlade
          open={uploadErrorsList.length > 0}
          setOpen={() => {}}
          onRetryUpload={handlePutCsv}
          onClose={() => setUploadErrorsList([])}
          errorsList={uploadErrorsList}
        />
        <PriceListsSideNav
          selectedPriceListId={selectedPriceListId}
          setSelectedPriceListId={setSelectedPriceListId}
          priceSheetList={priceSheetList}
        />
        <PriceListsSubContainer
          selectedPriceList={priceSheetList?.find(priceList => priceList.id === selectedPriceListId)}
          priceSheetList={priceSheetList}
        />
      </Grid>
    </>
  )
}

export default PriceListsContainer
